import { supabase } from './supabase';
import type { Database } from '../types/database.types';

export type ExploreTemplate = Database['public']['Tables']['video_templates']['Row'];
export type ExploreSlot = Database['public']['Tables']['video_template_slots']['Row'];
export type ExploreCategory = Database['public']['Tables']['video_template_categories']['Row'];
export type FeedSort = 'recent' | 'views' | 'likes' | 'saved';

export async function fetchCategories(): Promise<ExploreCategory[]> {
  const { data, error } = await supabase
    .from('video_template_categories')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchPublishedTemplates(args: {
  categoryId?: string | null;
  sort: FeedSort;
  search: string;
  userId?: string | null;
}): Promise<ExploreTemplate[]> {
  if (args.sort === 'saved') {
    if (!args.userId) return [];
    const { data: savedRows, error: savedError } = await supabase
      .from('video_template_saves')
      .select('template_id, created_at')
      .eq('user_id', args.userId)
      .order('created_at', { ascending: false })
      .limit(60);
    if (savedError) throw new Error(savedError.message);
    const ids = (savedRows || []).map((row) => row.template_id).filter(Boolean);
    if (!ids.length) return [];
    let query = supabase.from('video_templates').select('*').eq('status', 'published').in('id', ids);
    if (args.categoryId) query = query.eq('category_id', args.categoryId);
    const term = args.search.trim().replace(/[%_]/g, '').slice(0, 80);
    if (term) query = query.ilike('title', `%${term}%`);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    const byId = new Map((data || []).map((row) => [row.id, row]));
    return ids.map((id) => byId.get(id)).filter((row): row is ExploreTemplate => Boolean(row));
  }

  let query = supabase.from('video_templates').select('*').eq('status', 'published');
  if (args.categoryId) query = query.eq('category_id', args.categoryId);
  const term = args.search.trim().replace(/[%_]/g, '').slice(0, 80);
  if (term) query = query.ilike('title', `%${term}%`);
  if (args.sort === 'views') query = query.order('view_count', { ascending: false });
  else if (args.sort === 'likes') query = query.order('like_count', { ascending: false });
  else query = query.order('published_at', { ascending: false });
  const { data, error } = await query.limit(60);
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchPublishedTemplate(id: string): Promise<ExploreTemplate | null> {
  const { data, error } = await supabase
    .from('video_templates')
    .select('*')
    .eq('id', id)
    .eq('status', 'published')
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data;
}

export async function fetchHomeSlots(templateId: string): Promise<ExploreSlot[]> {
  const { data, error } = await supabase
    .from('video_template_slots')
    .select('*')
    .eq('template_id', templateId)
    .eq('home_visible', true)
    .order('sort_order', { ascending: true });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function fetchMyFlags(userId: string): Promise<{ likes: Set<string>; saves: Set<string> }> {
  const [likes, saves] = await Promise.all([
    supabase.from('video_template_likes').select('template_id').eq('user_id', userId),
    supabase.from('video_template_saves').select('template_id').eq('user_id', userId),
  ]);
  if (likes.error) throw new Error(likes.error.message);
  if (saves.error) throw new Error(saves.error.message);
  return {
    likes: new Set((likes.data || []).map((row) => row.template_id)),
    saves: new Set((saves.data || []).map((row) => row.template_id)),
  };
}

export async function setFlag(
  table: 'video_template_likes' | 'video_template_saves',
  userId: string,
  templateId: string,
  on: boolean,
): Promise<void> {
  if (on) {
    const { error } = await supabase.from(table).insert({ user_id: userId, template_id: templateId });
    if (error && !/duplicate|unique/i.test(error.message)) throw new Error(error.message);
    return;
  }
  const { error } = await supabase.from(table).delete().eq('user_id', userId).eq('template_id', templateId);
  if (error) throw new Error(error.message);
}

export async function recordTemplateView(templateId: string): Promise<void> {
  await supabase.rpc('increment_template_view', { p_template_id: templateId });
}

async function ensureFreshSession(): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  if (!session) throw new Error('Sua sessão expirou. Entre de novo e tente outra vez.');
  const expiresAtMs = (session.expires_at || 0) * 1000;
  // Only refresh when the access token is gone or about to expire (2 min).
  if (expiresAtMs && expiresAtMs - Date.now() > 120_000) return;
  try {
    await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('refresh-timeout')), 8_000)),
    ]);
  } catch {
    const { data: again } = await supabase.auth.getSession();
    if (!again.session) throw new Error('Sua sessão expirou. Entre de novo e tente outra vez.');
  }
}

async function invokeJson<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();

  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let message = error.message || 'Falha na requisição.';
    if (/Failed to send a request|Failed to fetch|Function not found/i.test(message)) {
      message = 'O estúdio ainda não está no ar.';
    }
    if (/Unauthorized|401|JWT|session/i.test(message)) {
      message = 'Sua sessão expirou. Entre de novo e tente outra vez.';
    }
    const context = (error as { context?: Response }).context;
    if (context && typeof context.json === 'function') {
      try {
        const payload = await context.json();
        if (payload && typeof payload.error === 'string') {
          message = /Unauthorized/i.test(payload.error)
            ? 'Sua sessão expirou. Entre de novo e tente outra vez.'
            : payload.error;
        }
      } catch {
        /* body already consumed or not json */
      }
    }
    throw new Error(message);
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    const err = String(data.error);
    throw new Error(/Unauthorized/i.test(err)
      ? 'Sua sessão expirou. Entre de novo e tente outra vez.'
      : err);
  }
  return data as T;
}

export async function remixTemplate(body: {
  organization_id: string;
  template_id?: string;
  intent?: 'remix' | 'preview' | 'revise' | 'stages';
  values?: Record<string, string>;
  source_job_id?: string;
  title?: string;
  instruction?: string;
  stage_batch_id?: string;
  image_model_id?: string;
  for_preview?: boolean;
  /** Criar igual: auto follows the uploaded photos. Preview ignores this. */
  aspect_mode?: 'auto' | 'horizontal' | 'vertical';
  model?: {
    video_model_id: string;
    resolution_id: string;
    duration_seconds: number;
    generate_audio: boolean;
  };
}): Promise<{
  job_id?: string;
  job_ids?: string[];
  stage_batch_id?: string;
  last_stage_url?: string;
  stage_count?: number;
  credits_charged: number;
  aspect_ratio?: string;
}> {
  return invokeJson('remix-template', body);
}

export async function studioCall<T>(body: Record<string, unknown>): Promise<T> {
  return invokeJson<T>('studio-template', body);
}

export async function isPlatformCreator(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('platform_creators')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return false;
  return Boolean(data);
}

export async function latestPreviewJob(templateId: string): Promise<{ id: string; status: string } | null> {
  const { data } = await supabase
    .from('jobs')
    .select('id, status')
    .eq('input->extras->>template_id', templateId)
    .eq('input->extras->>feature', 'template_preview')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data || null;
}
